// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import {
  buildCaseAddressLegendAnalysis,
  buildCaseAddressLegendEntriesFromCandidates,
  normalizeCaseAddressLegendAddressKey
} from './CaseBuddyAddressLegend.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { rowLooksLikeCaseBuddyHeader } from './CaseBuddySheetData.js';
import { CASE_DEFAULT_COLS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_TEMPLATE_TARGET_COLUMN_COUNT = 19;

export const CASE_TEMPLATE_TARGET_HEADERS = Object.freeze([
  'Path Label',
  'Transaction Type',
  'Transaction Hash',
  'Subpath / Parent Label',
  'From Type',
  'From Entity',
  'From Address',
  'From Legend Label',
  'Amount',
  'To Type',
  'To Entity',
  'To Address',
  'To Legend Label',
  'Address Risk',
  'Token',
  'Amount Copy',
  'UTC Date',
  'Reserved',
  'Notes'
]);

export const CASE_TEMPLATE_GAMBLING_OVERRIDE_KEYWORDS = Object.freeze(['Stake']);

export function getCaseTemplateCell(row = [], index = 0) {
  return normalizeCell(Array.isArray(row) ? row[index] : '');
}

export function setCaseTemplateCell(row = [], index = 0, value = '') {
  if (!Array.isArray(row) || index < 0 || index >= row.length) return;
  row[index] = normalizeCell(value);
}

export function caseTemplateColumnIndex(letter = '') {
  const safe = normalizeCell(letter).toUpperCase();
  return safe.length === 1 ? safe.charCodeAt(0) - 65 : -1;
}

export function caseTemplateIsWholeInteger(value = '') {
  return /^\d+$/.test(normalizeCell(value));
}

export function caseTemplateContainsIgnoreCase(haystack = '', needle = '') {
  const text = normalizeCell(haystack);
  const term = normalizeCell(needle);
  return !!(text && term && text.toLowerCase().includes(term.toLowerCase()));
}

export function getCaseTemplateSourceRows(mainData = []) {
  const data = normalizeCaseMatrix(mainData, 0, CASE_DEFAULT_COLS);
  const startIndex = data.length > 0 && rowLooksLikeCaseBuddyHeader(data[0]) ? 1 : 0;
  let lastContentIndex = -1;

  for (let rowIndex = startIndex; rowIndex < data.length; rowIndex += 1) {
    if (rowHasMeaningfulData(data[rowIndex]) && !rowLooksLikeCaseBuddyHeader(data[rowIndex])) {
      lastContentIndex = rowIndex;
    }
  }

  if (lastContentIndex < startIndex) return [];
  return data.slice(startIndex, lastContentIndex + 1).map(row => normalizeCaseMatrix([row], 1, CASE_DEFAULT_COLS)[0]);
}

export function getCaseTemplateLegendEntries(legendAnalysis = {}) {
  const storedEntries = Array.isArray(legendAnalysis.storedEntries) ? legendAnalysis.storedEntries : [];
  const liveEntries = Array.isArray(legendAnalysis.entries) ? legendAnalysis.entries : [];
  const candidateEntries = Array.isArray(legendAnalysis.candidates)
    ? buildCaseAddressLegendEntriesFromCandidates(legendAnalysis.candidates)
    : [];

  if (legendAnalysis.buildIsCurrent && storedEntries.length > 0) return storedEntries;
  if (liveEntries.length > 0) return liveEntries;
  return candidateEntries;
}

export function buildCaseTemplateLegendLookup(legendAnalysis = {}) {
  const lookup = new Map();
  getCaseTemplateLegendEntries(legendAnalysis).forEach((entry) => {
    const address = normalizeCell(entry.address || entry.Address || entry.preferredDisplayAddress);
    const label = normalizeCell(entry.label || entry.Label || entry.proposedLegendLabel || entry.displayLabel);
    const key = normalizeCaseAddressLegendAddressKey(address);
    if (key && label && !lookup.has(key)) lookup.set(key, label);
  });
  return lookup;
}

export function buildCaseTemplateExchangeKeywords(effectiveCurator = {}) {
  const keywords = [];
  const seen = new Set();
  const addKeyword = (value) => {
    const term = normalizeCell(value);
    if (!term) return;
    const key = term.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    keywords.push(term);
  };

  (Array.isArray(effectiveCurator.exchange) ? effectiveCurator.exchange : []).forEach((item) => {
    addKeyword(item?.name || item?.presentedName || item?.canonicalName || item);
  });

  keywords.sort((left, right) => {
    const lengthCompare = normalizeCell(left).length - normalizeCell(right).length;
    if (lengthCompare !== 0) return lengthCompare;
    return normalizeCell(left).localeCompare(normalizeCell(right), undefined, { sensitivity: 'base' });
  });

  return keywords;
}

export function applyCaseTemplateMappingRules(sourceRow = [], targetRow = []) {
  const rules = [
    ['A', 'A'],
    ['B', 'C'],
    ['C', 'G'],
    ['D', 'L'],
    ['E', 'Q'],
    ['F', 'P'],
    ['F', 'I'],
    ['G', 'O'],
    ['J', 'S']
  ];

  rules.forEach(([sourceColumn, targetColumn]) => {
    const sourceIndex = caseTemplateColumnIndex(sourceColumn);
    const targetIndex = caseTemplateColumnIndex(targetColumn);
    if (sourceIndex < 0 || targetIndex < 0) return;
    setCaseTemplateCell(targetRow, targetIndex, getCaseTemplateCell(sourceRow, sourceIndex));
  });
}

export function applyCaseTemplatePostMappingRules(row = []) {
  const valueInColumnA = getCaseTemplateCell(row, 0);
  const valueInColumnC = getCaseTemplateCell(row, 2);
  const valueInColumnG = getCaseTemplateCell(row, 6);
  const valueInColumnL = getCaseTemplateCell(row, 11);

  if (!valueInColumnA) {
    setCaseTemplateCell(row, 1, '');
    setCaseTemplateCell(row, 3, '');
  } else if (caseTemplateIsWholeInteger(valueInColumnA)) {
    setCaseTemplateCell(row, 1, 'Main TXN');
    setCaseTemplateCell(row, 3, 'Subpoenable Entity Path');
  } else {
    setCaseTemplateCell(row, 1, 'Sub TXN');
    setCaseTemplateCell(row, 3, valueInColumnA);
    setCaseTemplateCell(row, 0, '');
  }

  if (valueInColumnC) {
    setCaseTemplateCell(row, 4, 'Other');
    setCaseTemplateCell(row, 9, 'Other');
    setCaseTemplateCell(row, 13, 'Suspicious');
  }

  if (valueInColumnG) {
    setCaseTemplateCell(row, 4, 'Other');
    setCaseTemplateCell(row, 13, 'Suspicious');
  }

  if (valueInColumnL) {
    setCaseTemplateCell(row, 9, 'Other');
    setCaseTemplateCell(row, 13, 'Suspicious');
  }
}

export function applyCaseTemplateLegendRules(row = [], legendLookup = new Map()) {
  if (!(legendLookup instanceof Map) || legendLookup.size === 0) return;

  const fromKey = normalizeCaseAddressLegendAddressKey(getCaseTemplateCell(row, 6));
  const toKey = normalizeCaseAddressLegendAddressKey(getCaseTemplateCell(row, 11));

  if (fromKey && legendLookup.has(fromKey)) setCaseTemplateCell(row, 7, legendLookup.get(fromKey));
  if (toKey && legendLookup.has(toKey)) setCaseTemplateCell(row, 12, legendLookup.get(toKey));
}

export function applyCaseTemplateKeywordRules(row = [], exchangeKeywords = []) {
  const fromLegendLabel = getCaseTemplateCell(row, 7);
  const toLegendLabel = getCaseTemplateCell(row, 12);

  (Array.isArray(exchangeKeywords) ? exchangeKeywords : []).forEach((keyword) => {
    if (caseTemplateContainsIgnoreCase(fromLegendLabel, keyword)) {
      setCaseTemplateCell(row, 5, keyword);
      setCaseTemplateCell(row, 4, 'Exchange');
    }

    if (caseTemplateContainsIgnoreCase(toLegendLabel, keyword)) {
      setCaseTemplateCell(row, 10, keyword);
      setCaseTemplateCell(row, 9, 'Exchange');
      setCaseTemplateCell(row, 13, 'Exchange');
    }
  });

  CASE_TEMPLATE_GAMBLING_OVERRIDE_KEYWORDS.forEach((keyword) => {
    if (caseTemplateContainsIgnoreCase(fromLegendLabel, keyword)) {
      setCaseTemplateCell(row, 5, keyword);
      setCaseTemplateCell(row, 4, 'Gambling');
    }

    if (caseTemplateContainsIgnoreCase(toLegendLabel, keyword)) {
      setCaseTemplateCell(row, 10, keyword);
      setCaseTemplateCell(row, 9, 'Gambling');
      setCaseTemplateCell(row, 13, 'None');
    }
  });
}

export function buildCaseTemplateTargetRow(sourceRow = [], legendLookup = new Map(), exchangeKeywords = []) {
  const row = Array(CASE_TEMPLATE_TARGET_COLUMN_COUNT).fill('');
  applyCaseTemplateMappingRules(sourceRow, row);
  applyCaseTemplatePostMappingRules(row);
  applyCaseTemplateLegendRules(row, legendLookup);
  applyCaseTemplateKeywordRules(row, exchangeKeywords);
  return row;
}

export function buildCaseTemplateSummary(state = {}) {
  if (!state.hasAnyMainRows) return 'Template cannot be built yet because the case has no main spreadsheet rows.';
  if (!state.legendBuildIsCurrent) return 'Template needs a current address legend build.';
  if (!state.legendReviewIsCurrent) return 'Template needs a current address legend review.';
  return `Template is ready from ${state.rowCount} row${state.rowCount === 1 ? '' : 's'}, ${state.legendEntryCount} legend entr${state.legendEntryCount === 1 ? 'y' : 'ies'}, and ${state.exchangeKeywordCount} exchange keyword${state.exchangeKeywordCount === 1 ? '' : 's'}.`;
}

export function buildCaseTemplateState(mainData = [], effectiveCurator = {}, workflowState = {}, options = {}) {
  const sourceRows = getCaseTemplateSourceRows(mainData);
  const legendAnalysis = buildCaseAddressLegendAnalysis(
    mainData,
    effectiveCurator,
    workflowState,
    { labelDormantAddressesSeparately: !!options.labelDormantAddressesSeparately }
  );
  const legendLookup = buildCaseTemplateLegendLookup(legendAnalysis);
  const exchangeKeywords = buildCaseTemplateExchangeKeywords(effectiveCurator);
  const rows = sourceRows.map(sourceRow => buildCaseTemplateTargetRow(sourceRow, legendLookup, exchangeKeywords));
  const currentBasisHash = caseBuddyHashString(JSON.stringify({
    sourceRows,
    legendEntries: Array.from(legendLookup.entries()),
    exchangeKeywords,
    gamblingOverrides: CASE_TEMPLATE_GAMBLING_OVERRIDE_KEYWORDS
  }));

  const state = {
    caseId: normalizeCell(options.caseId),
    hasAnyMainRows: sourceRows.some(rowHasMeaningfulData),
    legendBuildIsCurrent: !!legendAnalysis.buildIsCurrent,
    legendReviewIsCurrent: !!legendAnalysis.reviewIsCurrent,
    buildIsCurrent: false,
    rowCount: rows.length,
    legendEntryCount: legendLookup.size,
    exchangeKeywordCount: exchangeKeywords.length,
    currentBasisHash,
    summary: '',
    rows,
    sourceRows,
    legendAnalysis
  };

  state.buildIsCurrent = state.hasAnyMainRows && state.legendBuildIsCurrent && state.legendReviewIsCurrent;
  state.summary = buildCaseTemplateSummary(state);
  return state;
}

export function buildCaseTemplateDeliverableRows(state = {}) {
  return (Array.isArray(state.rows) ? state.rows : []).map(row => normalizeCaseMatrix([row], 1, CASE_TEMPLATE_TARGET_COLUMN_COUNT)[0].slice(0, CASE_TEMPLATE_TARGET_COLUMN_COUNT));
}
